/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Cart, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

/** The most characters a guest can give a new order note. A longer note written before the limit is kept until it is edited. */
export const ORDER_NOTE_LIMIT = 25;

/** The Lumovy transaction comment, which the kitchen shows as the order's note. */
export const ORDER_NOTE_KEY = 'LS_TransactionComment';

export const readOrderNote = (cart?: Cart): string =>
    cart?.ExtensionProperties?.find(property => property.Key === ORDER_NOTE_KEY)?.Value?.StringValue ?? '';

/** Two notes are the same when they differ only in how their lines end, which a browser's text box changes. */
export const sameNote = (first: string, second: string): boolean => first.replace(/\r\n/g, '\n') === second.replace(/\r\n/g, '\n');

/**
 * The order note an order update sends: the cart's, only where the guest changed it since the order was read, so a note changed
 * at the till in the meantime is not overwritten.
 * @param cart - This channel's copy of the order.
 * @param heldNote - The note the store held when the order was last read.
 * @returns The header property to send, or none.
 */
export const orderNoteChange = (cart: Cart, heldNote: string): CommerceProperty[] => {
    const note = readOrderNote(cart);

    return sameNote(note, heldNote) ? [] : [{ Key: ORDER_NOTE_KEY, Value: { StringValue: note } }];
};
